import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useCfoRiskLandscape } from '../../../hooks/analytics/useCfoReports.ts';
import { WidgetCard } from '../controller/shared.tsx';
import { CHART_AXIS_LINE, CHART_AXIS_TICK, CHART_GRID_STROKE, CHART_LEGEND_STYLE, CHART_TOOLTIP_STYLE, chartColorFor } from '../theme.ts';

const LEVEL_COLORS: Record<string, string> = {
  high: chartColorFor('high'),
  medium: chartColorFor('medium'),
  med: chartColorFor('med'),
  low: chartColorFor('low'),
  unrated: chartColorFor('unrated'),
};

/**
 * Report #6 — Risk Landscape Summary.
 *
 * Field trap: the docx sketches a stacked-bar-by-cycle layout, but the real
 * `CFORiskLandscapeRow` (repository/analytics_reports_repository.go) has no
 * cycle dimension — it's `classification` (business/itgc) x `risk_level` x
 * `cnt`. The stacked bar groups by classification with risk_level segments
 * instead; the donut summarizes risk_level (High/Med/Low) across the org, as
 * spec'd.
 */
export const RiskLandscapeChart: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useCfoRiskLandscape();
  const rows = data ?? [];

  const levels = useMemo(
    () => Array.from(new Set(rows.map((r) => (r.risk_level ?? 'unrated').toLowerCase()))),
    [rows]
  );

  const stackedData = useMemo(() => {
    const byClass = new Map<string, Record<string, number | string>>();
    rows.forEach((row) => {
      const cls = row.classification ?? 'unclassified';
      const entry = byClass.get(cls) ?? { classification: cls };
      const level = (row.risk_level ?? 'unrated').toLowerCase();
      entry[level] = ((entry[level] as number) ?? 0) + row.cnt;
      byClass.set(cls, entry);
    });
    return Array.from(byClass.values());
  }, [rows]);

  const donutData = useMemo(() => {
    const byLevel = new Map<string, number>();
    rows.forEach((row) => {
      const level = (row.risk_level ?? 'unrated').toLowerCase();
      byLevel.set(level, (byLevel.get(level) ?? 0) + row.cnt);
    });
    return Array.from(byLevel.entries()).map(([level, value]) => ({ name: level, value }));
  }, [rows]);

  return (
    <WidgetCard
      title="Risk Landscape Summary"
      description="Risk counts by classification and risk level"
      loading={isLoading}
      error={isError ? error : null}
      errorMessage={error?.message ?? 'Failed to load risk landscape.'}
      isEmpty={!isLoading && !isError && rows.length === 0}
      emptyMessage="No risks found."
      updatedAt={dataUpdatedAt}
      onRefresh={refetch}
      isFetching={isFetching}
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={stackedData} margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID_STROKE} vertical={false} />
              <XAxis dataKey="classification" tick={CHART_AXIS_TICK} axisLine={CHART_AXIS_LINE} tickLine={false} />
              <YAxis tick={CHART_AXIS_TICK} axisLine={CHART_AXIS_LINE} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} cursor={{ fill: 'rgba(148, 163, 184, 0.08)' }} />
              <Legend wrapperStyle={CHART_LEGEND_STYLE} />
              {levels.map((level) => (
                <Bar
                  key={level}
                  dataKey={level}
                  name={level}
                  stackId="risk"
                  fill={LEVEL_COLORS[level] ?? chartColorFor(level)}
                  radius={levels.indexOf(level) === levels.length - 1 ? [4, 4, 0, 0] : undefined}
                  maxBarSize={48}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie data={donutData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={3}>
                {donutData.map((entry) => (
                  <Cell key={entry.name} fill={LEVEL_COLORS[entry.name] ?? chartColorFor(entry.name)} stroke="#fff" strokeWidth={2} />
                ))}
              </Pie>
              <Tooltip contentStyle={CHART_TOOLTIP_STYLE} />
              <Legend wrapperStyle={CHART_LEGEND_STYLE} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </WidgetCard>
  );
};

export default RiskLandscapeChart;
